/**
 * Tiny fetch wrapper for the admin SPA.
 *
 * - Prefixes `VITE_API_BASE_URL` (default `http://localhost:3000/api`).
 * - Auto-attaches `Authorization: Bearer <token>` from the auth store.
 * - Throws `ApiError` on non-2xx responses with the server's `error` code.
 * - On 401, calls the registered unauthorized-handler (logout) before
 *   throwing — so callers don't need to plumb auth into every request.
 */

import { ERROR_CODES } from '@shared/constants';

const DEFAULT_BASE_URL = 'http://localhost:3000/api';

// Vite STATICALLY replaces `import.meta.env.VITE_*` with the string literal
// at build time when the var is present in the active .env.<mode> file.
// Direct access is required — assigning import.meta to a renamed variable
// (e.g. `const meta = import.meta`) defeats the static replacement, and
// Vite's runtime env object does NOT carry VITE_* vars (only the built-ins
// MODE / BASE_URL / PROD / DEV / SSR), so the renamed-access fallback path
// silently lands on DEFAULT_BASE_URL in prod and breaks API calls.
const FROM_ENV = import.meta.env.VITE_API_BASE_URL;

export const API_BASE_URL: string =
  typeof FROM_ENV === 'string' && FROM_ENV.length > 0
    ? FROM_ENV
    : DEFAULT_BASE_URL;

export class ApiError extends Error {
  status: number;
  error: string;
  details: unknown;
  constructor(status: number, error: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.error = error;
    this.details = details;
  }
}

type TokenProvider = () => string | null;
type UnauthorizedHandler = () => void;
type RefreshHandler = () => Promise<string | null>;

let tokenProvider: TokenProvider = () => null;
let onUnauthorized: UnauthorizedHandler | null = null;
let refreshHandler: RefreshHandler | null = null;
// #618: X-Game-Id provider — the active game rides every admin call so the
// server's resolveGame middleware binds the right game before controllers
// run. Registered by the game store (same indirection as tokenProvider so
// this module stays Zustand-free).
let gameIdProvider: (() => string | null) | null = null;
let onGameUnavailable: (() => void) | null = null;

export function setGameIdProvider(p: () => string | null): void {
  gameIdProvider = p;
}

/**
 * Self-heal for a stranded active game (#618).
 *
 * `X-Game-Id` rides EVERY admin call, login and refresh included, and
 * `resolveGameMiddleware` 404s `game_not_found` for a game this deployment
 * does not admit. Persisted in localStorage, that is a lockout with no way
 * back through the UI: the switcher's snap-back only runs after a SUCCESSFUL
 * `/admin/games`, and that call carries the same poisoned header. So the
 * recovery has to hang off the failure itself.
 *
 * Deliberately keyed on `game_not_found` alone — `requireGameSurface` uses
 * its own `game_surface_not_found`, because "this game has no question bank"
 * is not a reason to throw away the operator's selection.
 */
export function setGameUnavailableHandler(h: () => void): void {
  onGameUnavailable = h;
}

export function setTokenProvider(p: TokenProvider): void {
  tokenProvider = p;
}

export function setUnauthorizedHandler(h: UnauthorizedHandler): void {
  onUnauthorized = h;
}

/**
 * Wire the refresh callback. Called by `api()` on a 401 with a token —
 * if it returns a fresh token the original request is retried once. The
 * auth store registers itself here so the API client doesn't have to know
 * about Zustand or the refresh-cookie endpoint.
 */
export function setRefreshHandler(h: RefreshHandler): void {
  refreshHandler = h;
}

export interface ApiInit extends Omit<RequestInit, 'body'> {
  body?: string | FormData;
  /** Skip the automatic Authorization header (e.g. for login). */
  skipAuth?: boolean;
  /** Internal flag — set when this call IS the retry, to prevent loops. */
  _isRefreshRetry?: boolean;
}

export async function api<T = unknown>(
  path: string,
  init: ApiInit = {},
): Promise<T> {
  const { skipAuth, headers, _isRefreshRetry, ...rest } = init;
  const finalHeaders = new Headers(headers);

  if (!finalHeaders.has('Content-Type') && rest.body && typeof rest.body === 'string') {
    finalHeaders.set('Content-Type', 'application/json');
  }
  finalHeaders.set('Accept', 'application/json');

  if (!skipAuth) {
    const token = tokenProvider();
    if (token) {
      finalHeaders.set('Authorization', `Bearer ${token}`);
    }
  }

  // #618: thread the active game. Explicit per-call headers win — a caller
  // that already set X-Game-Id keeps it.
  if (!finalHeaders.has('X-Game-Id')) {
    const gameId = gameIdProvider?.();
    if (gameId) {
      finalHeaders.set('X-Game-Id', gameId);
    }
  }

  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

  const response = await fetch(url, {
    ...rest,
    // `credentials: 'include'` is REQUIRED so the browser attaches the
    // HttpOnly `wc_admin_refresh` cookie on cross-origin XHR (Vite dev on
    // :5173 → API on :3000). In production both ride the same origin so
    // it's a no-op. The CORS server-side response sets
    // Access-Control-Allow-Credentials: true to match.
    credentials: 'include',
    headers: finalHeaders,
  });

  if (response.status === 401 && !skipAuth && !_isRefreshRetry) {
    // The access token is expired or revoked. Try to refresh exactly once;
    // if successful, replay the original request with the new token. If
    // refresh itself returns null (no cookie / expired refresh / reuse),
    // surface the 401 normally and let the unauthorized-handler clear UI
    // state.
    if (refreshHandler) {
      const newToken = await refreshHandler();
      if (newToken) {
        return api<T>(path, { ...init, _isRefreshRetry: true });
      }
    }
    onUnauthorized?.();
  }

  // No content
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const errCode =
      (payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error?: unknown }).error)
        : null) ?? `http_${response.status}`;
    const message =
      (payload && typeof payload === 'object' && 'message' in payload
        ? String((payload as { message?: unknown }).message)
        : null) ?? response.statusText ?? 'Request failed';
    if (response.status === 404 && errCode === ERROR_CODES.GAME_NOT_FOUND) {
      // The header we just sent names a game this deployment will not serve.
      // Drop it now, or every subsequent request — including the login this
      // operator needs — 404s for the life of the localStorage entry.
      onGameUnavailable?.();
    }
    throw new ApiError(response.status, errCode, message, payload);
  }

  return payload as T;
}
